import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { etykietaPokoiOferty } from '@/lib/pokoje';
import { daneMiekkieOferty, maZdjecia, niewiadomeOferty, zdanieNiewiadomych } from '@/lib/filtryMiekkie';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { ShieldCheck, Home } from 'lucide-react';
import { formatPostedRelative } from '@/lib/relativeTime';
import { stanNaRynku, wystawionaPonownie } from '@/lib/rynekOd';
import { formatLiczby } from '@/lib/i18nNumber';
import { cn } from '@/lib/utils';
import { PhotoCarousel } from './PhotoCarousel';
import { FavoriteButton } from './FavoriteButton';
import { PoiBadgeStrip } from './PoiBadgeStrip';
import { PastylkaWyroznikaMapy, PastylkiWyroznikow, WstazkaWyroznika } from '@/components/offer/Wyrozniki';
import { wyroznikWstazki, wyroznikiOferty } from '@/lib/wyroznikiOferty';
import { type SerpPost } from '@/hooks/useSourcePosts';
import { serpLocationLabel, serpNajwezszaNazwa } from '@/hooks/serpQuery';
import { isApproximateGeocode } from '@/lib/geocodePrecision';
import { kosztNajmu } from '@/lib/oplaty';
import { DymekNajazdu } from '@/components/ui/dymek-najazdu';

interface Props {
  post: SerpPost;
  /** Show the POI score badges (walkability/transit/green + okolica chips).
   * Off by default — the SERP offers a toggle to surface them as an extra line. */
  showPoiBadges?: boolean;
  /** Desktop hover-sync: fired with the post id on mouse enter, null on leave,
   * so the SERP can highlight the matching map pin (morizon/airbnb pattern). */
  onHover?: (id: string | null) => void;
  /**
   * Karta właśnie wskazana z mapy (etap F). Podświetlenie jest SUBTELNE — tło i obrys z
   * tokenów, bez ramki w kolorze akcentu na całej karcie (zasada Piotra z 27.09): karta ma
   * zostać kartą, a nie zamienić się w komunikat.
   */
  podswietlona?: boolean;
  /**
   * Karta jest na pierwszym ekranie — jej zdjęcie ładuje się od razu, a nie leniwie.
   * Lista ustawia to tylko kilku pierwszym kartom (krok N, wolne łącze).
   */
  pilneZdjecie?: boolean;
  /** Pierwsza karta listy — jej zdjęcie to element LCP (`fetchpriority=high`). */
  zdjecieLcp?: boolean;
  /**
   * Widok filtruje po KOSZCIE najmu (przełącznik w filtrze ceny). Karta pokazuje wtedy
   * koszt jako dużą liczbę, a czynsz mniejszym drukiem — człowiek porównuje to, po czym
   * filtruje (decyzja Piotra 28.09).
   */
  trybKosztu?: boolean;
  /**
   * Aktywne FILTRY MIĘKKIE (zwierzęta, umeblowanie, piętro, kuchnia, łazienki…). Oferta, o
   * której ogłoszenie w danej sprawie milczy, dostaje jedno zdanie „Ogłoszenie nie mówi nic o …
   * — zapytaj właściciela" zamiast ciszy: człowiek musi wiedzieć, czy to „nie wiadomo", czy
   * „tak". Oferty wprost niepasującej tu nie ma — łańcuch filtrów ją odciął.
   */
  filtryMiekkie?: SourcePostFilters;
  /**
   * `lista` — kafelek w wynikach (pionowy, duże zdjęcie).
   * `mapa` — ta sama karta w karuzeli NAD MAPĄ: pozioma, zdjęcie z lewej.
   *
   * To nie jest inna karta, tylko inny układ tej samej: te same dane, ta sama cena, to samo
   * serduszko, te same napisy z i18n. Powód jest zmierzony: na iPhonie widać 314 px mapy, a
   * pionowa karta ma 405 px — karta „nad mapą" zasłaniałaby mapę w całości, czyli dokładnie to,
   * po co tu jest.
   *
   * W układzie mapy NIE MA karuzeli zdjęć, i to też jest decyzja, nie brak: karty przewija się
   * w bok, a karuzela zdjęć przewija się w bok — dwa te same gesty jeden na drugim znaczą, że
   * żaden nie działa pewnie. Zdjęcie jest jedno, reszta czeka na stronie oferty.
   */
  wariant?: 'lista' | 'mapa';
}

export const SourcePostCard = memo(function SourcePostCard({
  post,
  showPoiBadges = false,
  onHover,
  podswietlona = false,
  pilneZdjecie = false,
  zdjecieLcp = false,
  trybKosztu = false,
  filtryMiekkie,
  wariant = 'lista',
}: Props) {
  const { t, i18n } = useTranslation();

  const rent = post.extracted_rent_pln;
  const rooms = post.extracted_rooms;
  const area = post.extracted_area_m2;
  const locationLabel = serpLocationLabel(
    post.extracted_city,
    post.extracted_suburb,
    post.extracted_subdistrict,
  );
  const postedRelative = post.posted_at
    ? formatPostedRelative(post.posted_at, i18n.language, t)
    : null;
  /**
   * „NA RYNKU OD N DNI" — druga połowa linii z datą (decyzja Piotra 27.09). Data publikacji
   * mówi, kiedy pojawiło się TO ogłoszenie; czas na rynku mówi, jak długo mieszkanie nie
   * schodzi — a to jest informacja, po której najemca pyta o cenę. Reguła (próg 14 dni,
   * sufit korpusu) siedzi w `rynekOd.ts`; tutaj zostaje wybór napisu.
   */
  // Na karcie tylko przy ofercie wystawionej ponownie (Piotr 7.10, `wystawionaPonownie`);
  // zwykła oferta ma samą datę. Strona oferty pokazuje „na rynku od” zawsze.
  const naRynku = wystawionaPonownie(post.market_since, post.posted_at) ? stanNaRynku(post.market_since) : null;
  const naRynkuNapis = naRynku
    ? t(naRynku.ponad ? 'search.card.on_market_over' : 'search.card.on_market', { count: naRynku.dni })
    : null;
  const liniaDaty = [postedRelative, naRynkuNapis].filter(Boolean).join(' · ');

  // "Może być nieaktualne" now flags ONLY confirmed dead/sold listings — which the
  // default SERP hides, so it surfaces only via the dev `includeDead` lens. The old
  // age heuristic (>5 days) was dropped: the card already shows "X dni temu", so the
  // warning was redundant noise on still-live older posts.
  const maybeStale = post.marketplace_is_sold === true || post.stale_at != null;

  const headline = locationLabel || t('search.card.headline_fallback');

  // Wyróżniki: jedna wstążka według `KOLEJNOSC_WSTAZKI_KARTY` (Piotr 7.10), reszta w pastylkach;
  // cecha z filtra idzie do pastylek z ✓ (wariant 2). Reguły i testy: src/lib/wyroznikiOferty.ts.
  const wyrozniki = wyroznikiOferty(post, { filtry: filtryMiekkie });
  const wstazkaKarty = wyroznikWstazki(wyrozniki);

  const koszt = kosztNajmu(rent, (post.extracted_extras ?? {}) as Record<string, unknown>, {
    total_monthly_cost_pln: post.total_monthly_cost_pln,
    admin_fee_source: post.admin_fee_source,
  });

  /**
   * CO JEST DUŻĄ LICZBĄ NA KARCIE, ZALEŻY OD TRYBU (decyzja Piotra 28.09).
   *
   * W trybie „Cena najmu" duża liczba to CZYNSZ, a koszt idzie drugą linią — tak jak
   * dotąd. W trybie „Koszt najmu" jest odwrotnie: człowiek filtruje po koszcie, więc
   * porównuje koszty, a karta pokazująca wtedy czynsz kazałaby mu dodawać w pamięci.
   * Czynsz zostaje mniejszym drukiem, bo to on stoi w ogłoszeniu i po nim rozpoznaje się
   * ofertę u źródła.
   *
   * Gdy w trybie kosztu kosztu NIE ZNAMY (opłaty nieznane), duża liczba wraca do czynszu —
   * lepiej pokazać prawdziwy czynsz niż udawać koszt. Takie oferty i tak wypadają z filtra
   * kosztu (SRCH-15), więc zobaczy je tylko ktoś bez ustawionej ceny.
   */
  const kosztJakoGlowna = trybKosztu && koszt.pln != null;
  const glownaKwota = kosztJakoGlowna ? koszt.pln! : rent;

  /**
   * SZACUNEK = KWOTA Z TYLDĄ, A KWOTA JEST WYZWALACZEM DYMKA (Piotr 2.10). Do 2.10 było „ok. …”
   * plus ikona ⓘ z natywnym `title`, który pokazywał się dopiero po długim przytrzymaniu myszy,
   * a na telefonie wcale. Teraz najechanie otwiera dymek od razu, dotknięcie przełącza; kropkowane
   * podkreślenie mówi, że pod kwotą coś jest. Klik w kwotę ani w dymek nie przenosi na stronę
   * oferty (karta jest odnośnikiem; `wewnatrzLinku`).
   */
  const zDymkiemSzacunku = (tekst: string, klasa: string) => (
    <DymekNajazdu
      wewnatrzLinku
      className="w-64 max-w-[calc(100vw-2rem)] p-3 text-sm text-muted-foreground"
      data-dymek-szacunku=""
      wyzwalacz={
        <button
          type="button"
          data-szacunek-kosztu=""
          className={cn(
            'cursor-help rounded-sm underline decoration-muted-foreground decoration-dotted underline-offset-2',
            'outline-none focus-visible:ring-2 focus-visible:ring-primary',
            klasa,
          )}
        >
          {tekst}
        </button>
      }
    >
      {t('search.card.fees_estimate_tooltip')}
    </DymekNajazdu>
  );

  const cena =
    glownaKwota != null ? (
      <>
        {kosztJakoGlowna && koszt.zrodlo === 'estimated' ? (
          zDymkiemSzacunku(
            t('search.card.cost_big_estimated', { value: formatLiczby(glownaKwota, i18n.language) }),
            'text-base font-bold',
          )
        ) : (
          <span className="text-base font-bold">
            {t('search.card.price_pln_value', { value: formatLiczby(glownaKwota, i18n.language) })}
          </span>
        )}{' '}
        <span className="text-sm text-muted-foreground">{t('search.card.price_pln_unit')}</span>
      </>
    ) : rent != null ? (
      <>
        <span className="text-base font-bold">
          {t('search.card.price_pln_value', { value: formatLiczby(rent, i18n.language) })}
        </span>{' '}
        <span className="text-sm text-muted-foreground">{t('search.card.price_pln_unit')}</span>
      </>
    ) : (
      <span className="text-sm text-muted-foreground">{t('search.card.price_unknown')}</span>
    );

  /**
   * DRUGA LINIA. W trybie ceny mówi, ile to jest z opłatami (krok Q); w trybie kosztu
   * odwrotnie — podaje sam czynsz, bo dużą liczbą jest już koszt. Stany źródła opłat:
   *  - podane i koszt ≠ cena najmu → kwota;
   *  - szacunek → kwota z tyldą, która otwiera dymek z wyjaśnieniem (`zDymkiemSzacunku`);
   *  - znane i koszt = cena najmu → „czynsz administracyjny w cenie” (Piotr 2.10: w OBU trybach;
   *    do 2.10 w trybie ceny linii wtedy nie było, a powtórzona kwota wyglądała na usterkę);
   *  - nieznane → linii nie ma wcale, bo zgadywanie podane jako liczba jest gorsze niż jej brak.
   *    `koszt.pln` jest wtedy null, więc „w cenie” też się nie pokaże.
   * Linia ma zawsze 16 px (`leading-4`): wyzwalacz dymka to zwykły tekst bez ikony.
   */
  const wCenie = koszt.pln != null && rent != null && !koszt.rozniSieOdCzynszu;
  const trescLiniiKosztu: React.ReactNode = kosztJakoGlowna ? (
    rent != null ? (
      wCenie
        ? t('search.card.fees_included')
        : t('search.card.rent_line', { value: formatLiczby(rent, i18n.language) })
    ) : null
  ) : koszt.pln != null && koszt.rozniSieOdCzynszu ? (
    koszt.zrodlo === 'estimated'
      ? zDymkiemSzacunku(
          t('search.card.total_with_fees_estimated', { value: formatLiczby(koszt.pln, i18n.language) }),
          '',
        )
      : t('search.card.total_with_fees', { value: formatLiczby(koszt.pln, i18n.language) })
  ) : wCenie ? (
    t('search.card.fees_included')
  ) : null;
  // Sama linia kosztu, bez dopisków (Piotr 7.10): media i parking w cenie są wyróżnikami
  // (pastylka albo wstążka), a pełna lista opłat stoi na stronie oferty (OfferCostsSection).
  const liniaKosztu =
    trescLiniiKosztu != null ? (
      <p data-linia-kosztu="" className="whitespace-nowrap text-xs leading-4 text-muted-foreground">
        {trescLiniiKosztu}
      </p>
    ) : null;

  if (wariant === 'mapa') {
    // Nad mapą kontekst widać — mapa NIM jest — więc tytuł niesie samo osiedle. „Śródmieście
    // ·…" ucinało w tym miejscu jedyną nową informację (zgłoszone po zrzucie z etapu 2).
    const nazwaNaMapie =
      serpNajwezszaNazwa(post.extracted_city, post.extracted_suburb, post.extracted_subdistrict) ??
      headline;
    return (
      <Link
        to={`/offer/${post.id}`}
        state={{ post, filtry: filtryMiekkie }}
        className="group flex gap-3 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <div className="relative h-28 w-28 shrink-0">
          <PhotoCarousel
            photos={post.photos?.slice(0, 1)}
            photoSources={post.photoSources?.slice(0, 1)}
            alt={headline}
            aspect="square"
            rounded
          />
          {/* Bez `scale-90`: skala zmniejszała też strefę dotyku (40 → 36 px). Ten sam wygląd
              (32 px, ikona 22 px) z wymiarów, nie z transformacji. */}
          <FavoriteButton offerId={post.id} className="absolute right-1.5 top-1.5 p-[5px]" rozmiar="h-[22px] w-[22px]" />
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <h3 className="line-clamp-1 text-[15px] font-semibold leading-snug text-foreground">
            {nazwaNaMapie}
          </h3>
          {(rooms != null || area != null) && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {[
                etykietaPokoiOferty(rooms, t),
                area != null ? t('search.card.area', { value: area }) : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <p className="mt-1 text-foreground">{cena}</p>
          {/* DRUGA LINIA: KOSZT NAJMU (wzór QuintoAndar: „R$ 2.310 aluguel / R$ 2.724
              total"). Ta sama liczba i to samo słowo, co w filtrze i na stronie oferty.
              Pokazujemy ją TYLKO wtedy, gdy różni się od czynszu: „2 800 zł / 2 800 zł z
              opłatami" nie niesie niczego poza szumem. */}
          {liniaKosztu}
          {/* Wyróżniki nad mapą: jedna pastylka + „+N” (wariant B, 30.09) — wstążka na miniaturze
              112 px łamała się na dwie linie i zasłaniała zdjęcie. */}
          <PastylkaWyroznikaMapy lista={wyrozniki} />
          {postedRelative && (
            <p className="mt-1 text-[11px] text-muted-foreground">{postedRelative}</p>
          )}
          {/* BEZ ZDJĘĆ (Piotr 29.09, tekst W1): nad mapą nie ma drugiej kreski z listy, więc sam
              domek w miniaturze nie mówi, co zrobić — to zdanie mówi. */}
          {!maZdjecia(post) && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-bez-zdjec>
              {t('search.map.bez_zdjec_zapytaj')}
            </p>
          )}
          {/* „Lokalizacja przybliżona" NA KARCIE, bo na mapie niesie to już tylko pusta kropka
              (decyzja Piotra 27.09: jeden kolor pigułki, koniec kodowania kolorem). Bez tego
              zdania człowiek nie ma skąd wiedzieć, że pin stoi na środku dzielnicy, a nie pod
              adresem. */}
          {isApproximateGeocode(post.geocode_provider, post.extracted_lat != null) && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t('search.map.approximate_location')}
            </p>
          )}
          {/* Plakietki zaufania zostają, bo to one odróżniają ofertę właściciela od zeskrobanej
              — ale jako jedna linijka pod treścią, nie jako nakładki na miniaturze 112 px.
              „Agencji” na karcie nie ma (Piotr 1.10): to nie zaleta oferty; zostaje na stronie oferty. */}
          {(post.owner_confirmed || post.owner_present) && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
              {post.owner_confirmed ? (
                <>
                  <ShieldCheck className="h-3.5 w-3.5 text-success" />
                  {t('search.card.owner_verified')}
                </>
              ) : post.owner_present ? (
                <>
                  <Home className="h-3.5 w-3.5" />
                  {t('search.card.owner_on_platform')}
                </>
              ) : null}
            </p>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/offer/${post.id}`}
      // Filtry jadą do oferty: sekcja „Co wyróżnia to mieszkanie” zaznacza, co pasuje do wyszukiwania.
      state={{ post, filtry: filtryMiekkie }}
      data-oferta={post.id}
      onMouseEnter={() => onHover?.(post.id)}
      // Ruch po karcie też zgłasza najazd: po przewinięciu listy kursor stoi już na karcie, a
      // działanie wznawia dopiero ruch myszy (src/lib/najazdKarty.ts); wejścia wtedy nie będzie.
      onMouseMove={onHover ? () => onHover(post.id) : undefined}
      onMouseLeave={() => onHover?.(null)}
      // Znacznik dla pomiarów i bramek — NIE klasa. Klasa opisuje wygląd i wolno ją zmienić
      // (i właśnie się zmieniła: `bg-accent` malowało całą kartę mocnym pomarańczem
      // rgb(209,136,71), czyli dokładnie „ramką w kolorze akcentu", której Piotr nie chciał).
      data-podswietlona={podswietlona ? '1' : undefined}
      className={cn(
        // `karta-leniwa` = content-visibility: auto — karta daleko od okna nie jest malowana.
        // Powód i pomiary stoją przy tej klasie w src/index.css.
        'karta-leniwa group group/karta block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        // Podświetlenie SUBTELNE: ten sam token akcentu, ale 15% krycia plus obrys z `border`.
        // Sygnał „to ta karta" ma być widoczny kątem oka i zniknąć po chwili, a nie przemalować
        // kartę na pomarańczowo.
        podswietlona && 'bg-accent/15 ring-1 ring-border transition-colors duration-300',
      )}
    >
      <div className="relative">
        <PhotoCarousel
          photos={post.photos}
          photoSources={post.photoSources}
          videoClickUrl={post.fb_video_url}
          alt={headline}
          aspect="card"
          rounded
          showArrows
          pilne={pilneZdjecie}
          najwazniejsze={zdjecieLcp}
        />
        <FavoriteButton offerId={post.id} className="absolute right-3 top-3" />
        {/* JEDNA rzecz przy lewej krawędzi zdjęcia, zawsze w tym samym miejscu (Piotr 30.09).
            Kolejność: zweryfikowany → właściciel na FlatHero → taniej → zwierzęta.
            Trust ladder (Piotr 2026-07-23) zostaje: zweryfikowany ma zieloną tarczę, właściciel
            neutralny domek — tyle że w kształcie wstążki, nie osobnej białej pastylki.
            „Agencji” na wstążce nie ma (Piotr 1.10): wstążka niesie zaletę oferty, a „od agencji”
            nią nie jest; informacja zostaje na stronie oferty. Plakietek dostępności i
            „Również na {{portal}}” na karcie nie ma (decyzje 27.09 i DEC-04 26.09). */}
        {wstazkaKarty && <WstazkaWyroznika w={wstazkaKarty} />}
      </div>

      <div className="mt-3 px-1">
        {/* Location (dzielnica · osiedle · pokoje) in a FIXED 2-line slot so the
            osiedle is never mid-word truncated AND every card is the same height
            regardless of name length: line-clamp-2 caps at 2 lines, min-h reserves
            2 lines even for short names. The update time drops to a smaller line
            below — secondary to where the flat actually is. */}
        <h3 className="line-clamp-2 min-h-[2.625rem] text-[15px] font-semibold leading-snug text-foreground">
          {headline}
        </h3>
        {/* Spec: pokoje · m² on one line. */}
        {(rooms != null || area != null) && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            {[
              etykietaPokoiOferty(rooms, t),
              area != null ? t('search.card.area', { value: area }) : null,
            ].filter(Boolean).join(' · ')}
          </p>
        )}
        {/* Price — the prominent anchor (qasa/airbnb lead with it). Duża liczba to CZYNSZ,
            pod nią koszt najmu z opłatami (krok Q). Do 27.09 druga linia była tylko na
            karcie nad mapą, więc ta sama oferta mówiła w liście i na mapie co innego. */}
        <p className="mt-1.5 text-foreground">{cena}</p>
        {/* STAŁE MIEJSCA (Piotr 30.09: „data dodania zawsze w tym samym miejscu”). Linia kosztu
            i rząd pastylek rezerwują swoją wysokość także wtedy, gdy są puste — inaczej karta
            bez kosztu albo bez wyróżników podciąga datę o linijkę i rząd kart się rozjeżdża. */}
        <div className="min-h-4">{liniaKosztu}</div>
        <div className="min-h-[1.625rem]" data-miejsce-pastylek>
          <PastylkiWyroznikow lista={wyrozniki} naWstazce={wstazkaKarty} />
        </div>
        {/* Dopisek „wcześniej X zł” (z `originalPrice`) usunięty 7.10: obniżka to wyróżnik
            „Cena obniżona” z kolumn projekcji, bez przekreślania na karcie; podwyżek nie ma nigdzie. */}
        {/* Freshness — secondary, under the price. */}
        {/* ZWIERZĘTA: jedno zdanie zamiast ciszy. Pokazujemy je TYLKO szukającemu ze
            zwierzęciem i TYLKO przy ofercie bez deklaracji — przy „tak" jest plakietka,
            przy „nie" oferty tu nie ma (filtr ją odciął). Pokrycie: 71% ogłoszeń nie mówi
            o zwierzętach nic (pomiar agenta danych 28.09), więc bez tego zdania większość
            wyników wygląda na obojętną, a nie na niewiadomą. */}
        {filtryMiekkie && (() => {
          const zdanie = zdanieNiewiadomych(
            // Winda i kaucja z KOLUMN (ścieżka rankingu je dokłada): `extras.has_elevator`
            // rozjeżdża się z kolumną w 1 632 ofertach, a to kolumnę filtruje baza.
            // Te same dane co kreska na liście — `daneMiekkieOferty` (kolumny, nie `extras`).
            niewiadomeOferty(filtryMiekkie, daneMiekkieOferty(post as Parameters<typeof daneMiekkieOferty>[0])),
            t,
            i18n.language,
          );
          return zdanie ? (
            <p data-testid="nie-wiadomo" className="mt-1 text-[11px] text-muted-foreground">{zdanie}</p>
          ) : null;
        })()}
        {liniaDaty && (
          <p className="mt-1 text-[11px] text-muted-foreground">{liniaDaty}</p>
        )}
        {maybeStale && (
          <p className="mt-0.5 text-xs text-warning">
            {post.marketplace_is_sold === true
              ? t('search.card.sold')
              : t('search.card.maybe_stale')}
          </p>
        )}
        {showPoiBadges && (
          <div className="mt-2">
            <PoiBadgeStrip
              walkability={post.walkability_score}
              transit={post.transit_score}
              green={post.green_score}
              badges={post.poi_badges}
              metroNearestName={post.metro_nearest_name}
            />
          </div>
        )}
      </div>
    </Link>
  );
});
